import { z } from 'zod';
import { PermissionMap } from './permissions';
import { Vertical } from './account';

export const LoginRequest = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const TokenPair = z.object({
  access: z.string(),
  refresh: z.string(),
  user: z.object({ id: z.string(), fullName: z.string(), isOwner: z.boolean() }),
});
export type TokenPair = z.infer<typeof TokenPair>;

export const RefreshRequest = z.object({ refresh: z.string().min(32) });
export type RefreshRequest = z.infer<typeof RefreshRequest>;

/** Вход на кассе: PIN сотрудника на активированном терминале. */
export const PosLoginRequest = z.object({
  deviceKey: z.string().min(16),
  pin: z.string().regex(/^\d{4,6}$/),
});
export type PosLoginRequest = z.infer<typeof PosLoginRequest>;

export const PosSession = z.object({
  token: z.string(),
  user: z.object({ id: z.string(), fullName: z.string() }),
  /** Права роли кассира на этой точке — касса знает их до первого действия. */
  permissions: PermissionMap,
  /** потолок ручной скидки, %; null — без лимита (жёстче из ролей точки) */
  discountLimitPct: z.number().int().nullable(),
  /** «приведи друга» на аккаунте; null — выключено */
  referral: z.object({ everyVisits: z.number().int(), percent: z.number().int() }).nullable(),
  /** адрес витрины точки — для QR «как вам?» на чеке */
  menuUrl: z.string().nullable(),
});
export type PosSession = z.infer<typeof PosSession>;

/** Отметка ухода: тот же PIN, что и вход, но явка закрывается. */
export const PosClockOutRequest = z.object({
  deviceKey: z.string().min(16),
  pin: z.string().regex(/^\d{4,6}$/),
});
export type PosClockOutRequest = z.infer<typeof PosClockOutRequest>;

export const PosClockOutResponse = z.object({
  fullName: z.string(),
  workedMin: z.number().int(),
});
export type PosClockOutResponse = z.infer<typeof PosClockOutResponse>;

/** Самообслуживание: заведение появляется без нашего участия. */
export const SignupRequest = z.object({
  accountName: z.string().min(2).max(60),
  vertical: Vertical,
  locationName: z.string().min(2).max(60).default('Основная точка'),
  ownerName: z.string().min(2).max(60),
  email: z.string().email(),
  password: z.string().min(8).max(72),
  /** PIN владельца — чтобы сразу войти и в кассу */
  ownerPin: z.string().regex(/^\d{4}$/),
  /**
   * Телефон владельца — единственный надёжный ключ живого человека.
   * По нему платформа ловит повторную регистрацию того же заведения
   * и споры партнёров «мой клиент — нет, мой»
   */
  ownerPhone: z.string().min(10).max(20),
  /** город — чтобы одобряющий понимал, кого пускает */
  city: z.string().min(2).max(40).optional(),
});
export type SignupRequest = z.infer<typeof SignupRequest>;

export const SignupResponse = z.object({
  tokens: TokenPair,
  /** код активации первой кассы — показывается один раз */
  activationCode: z.string(),
});
export type SignupResponse = z.infer<typeof SignupResponse>;

/** Открытые вызовы со столов — полоска на кассе. */
export const OpenCalls = z.object({
  rows: z.array(z.object({
    id: z.string(),
    tableName: z.string(),
    kind: z.enum(['WAITER', 'BILL']),
    at: z.string().datetime(),
  })),
});
export type OpenCalls = z.infer<typeof OpenCalls>;
